import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { consultationService, appointmentService, doctorService, patientService } from '@/services/api';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import { utcToKolkataParts, formatDate, formatTime } from '@/utils/dateTime';
import { formatPatientAge, formatPhone, cn, formatDoctorName, getDoctorInitials } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Search,
  Stethoscope,
  Clock,
  User,
  Calendar,
  ArrowRight,
  CheckCircle2,
  Hourglass,
  Activity,
  Plus,
  Play,
  FileText,
  Building2,
  AlertCircle,
  Filter,
  Receipt,
} from 'lucide-react';
import { InvoiceModal } from '@/components/billing/InvoiceModal';
import { Button } from '@/components/ui/button';
import { AuthorizedButton } from '@/components/common/AuthorizedButton';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const ConsultationListPage = () => {
  const navigate = useNavigate();
  const { activeClinicId, activeClinic, buildUrl, hospitalId } = useClinic();
  const { user, hasRole, hasPermission } = useAuth();

  const canViewAllConsultations = hasPermission('CONSULTATION_ALL_VIEW') || hasPermission('PLATFORM_MANAGE');

  const [queue, setQueue] = useState({ waiting: [], inProgress: [], completed: [], totalToday: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState(canViewAllConsultations ? 'all' : (user?.id || 'all'));
  const [doctors, setDoctors] = useState([]);
  const [patientsMap, setPatientsMap] = useState({});
  const [activeTab, setActiveTab] = useState('waiting');
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedItemForInvoice, setSelectedItemForInvoice] = useState(null);

  const doctorsMap = useMemo(() => {
    const map = {};
    (doctors || []).forEach((d) => {
      if (d.id) map[d.id] = d;
    });
    return map;
  }, [doctors]);

  useEffect(() => {
    loadDoctors();
    loadPatients();
  }, []);

  useEffect(() => {
    loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClinicId, selectedDoctor]);

  const loadDoctors = async () => {
    try {
      const res = await doctorService.getAll();
      setDoctors(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadPatients = async () => {
    try {
      const res = await patientService.getAll({ limit: 100 });
      const initialMap = {};
      (res.data || []).forEach((p) => {
        if (p.id) initialMap[p.id] = p;
      });
      setPatientsMap((prev) => ({ ...initialMap, ...prev }));
    } catch (e) {
      console.error('Failed to pre-fetch patients list', e);
    }
  };

  const loadQueue = async () => {
    try {
      setLoading(true);
      const data = await consultationService.getQueue(activeClinicId, selectedDoctor !== 'all' ? selectedDoctor : undefined);
      setQueue(data);

      // Collect any patientIds that are not yet in patientsMap
      const allItems = [...(data.waiting || []), ...(data.inProgress || []), ...(data.completed || [])];
      const missingPatientIds = Array.from(
        new Set(
          allItems
            .map((item) => item.patientId || item.patient?.id)
            .filter((pid) => pid && !patientsMap[pid])
        )
      );

      if (missingPatientIds.length > 0) {
        Promise.allSettled(
          missingPatientIds.map((pid) => patientService.getById(pid))
        ).then((results) => {
          const fetchedMap = {};
          results.forEach((res) => {
            if (res.status === 'fulfilled' && res.value?.id) {
              fetchedMap[res.value.id] = res.value;
            }
          });
          if (Object.keys(fetchedMap).length > 0) {
            setPatientsMap((prev) => ({ ...prev, ...fetchedMap }));
          }
        });
      }
    } catch (error) {
      toast.error('Failed to load consultation queue');
    } finally {
      setLoading(false);
    }
  };

  const getPatientInfo = (item) => {
    const patient = item.patient || {};
    const matchedPatient = patientsMap[item.patientId || patient.id] || {};

    const rawName =
      (patient.name && patient.name !== 'Unknown' && patient.name !== 'Patient')
        ? patient.name
        : (item.patientName && item.patientName !== 'Unknown' && item.patientName !== 'Patient'
            ? item.patientName
            : (matchedPatient.name || `${matchedPatient.firstName || ''} ${matchedPatient.lastName || ''}`.trim() || `${patient.firstName || ''} ${patient.lastName || ''}`.trim() || ''));

    const pParts = (rawName || '').split(' ').filter(Boolean);
    const firstName = (patient.firstName && patient.firstName !== 'Patient')
      ? patient.firstName
      : (matchedPatient.firstName || pParts[0] || '');
    const lastName = patient.lastName || matchedPatient.lastName || (pParts.length > 1 ? pParts.slice(1).join(' ') : '');
    const displayName = rawName || (firstName ? `${firstName} ${lastName}`.trim() : '');

    const patientCode = patient.patientCode || matchedPatient.patientCode || item.patientCode || '';
    const age = patient.age ?? matchedPatient.age ?? item.patientAge ?? item.age;
    const dateOfBirth = patient.dateOfBirth || matchedPatient.dateOfBirth || item.patientDateOfBirth || item.dateOfBirth;
    const phone = patient.phone || matchedPatient.phone || item.patientPhone || item.phone || '';

    return {
      displayName: displayName || (patientCode ? `Patient (${patientCode})` : (item.patientId ? `Patient #${String(item.patientId).slice(0, 8)}` : 'Patient')),
      firstName: firstName || displayName?.split(' ')[0] || 'P',
      lastName: lastName || displayName?.split(' ')[1] || '',
      patientCode,
      age,
      dateOfBirth,
      phone,
    };
  };

  const getDoctorInfo = (item) => {
    const doctor = item.doctor || {};
    const matchedDoctor = doctorsMap[item.doctorId || doctor.id] || {};
    const doctorTarget = (doctor.name || doctor.firstName) ? doctor : ((matchedDoctor.name || matchedDoctor.firstName) ? matchedDoctor : (item.doctorName || doctor));
    const displayName = formatDoctorName(doctorTarget, 'Dr. Duty Physician');
    const specialization = matchedDoctor.specialization || doctor.specialization || item.doctorSpecialization || '';

    return {
      displayName,
      specialization,
    };
  };

  const handleStartConsultation = async (appointmentId, patientId) => {
    try {
      await appointmentService.updateStatus(appointmentId, 'IN_PROGRESS');
      navigate(buildUrl(`consultations/${appointmentId}?appointmentId=${appointmentId}&patientId=${patientId}`));
    } catch (error) {
      toast.error('Failed to start consultation');
    }
  };

  const filterList = (list) => {
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(item => {
      const pInfo = getPatientInfo(item);
      const dInfo = getDoctorInfo(item);
      return (
        pInfo.displayName?.toLowerCase().includes(q) ||
        pInfo.patientCode?.toLowerCase().includes(q) ||
        item.patientId?.toLowerCase().includes(q) ||
        dInfo.displayName?.toLowerCase().includes(q) ||
        dInfo.specialization?.toLowerCase().includes(q)
      );
    });
  };

  const waitingList = filterList(queue.waiting);
  const inProgressList = filterList(queue.inProgress);
  const completedList = filterList(queue.completed);

  return (
    <div className="space-y-6 max-w-7xl" data-testid="consultation-queue-page">
      {/* Header & Quick Action */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-heading font-bold text-slate-900">
              Clinical OPD Queue
            </h1>
            <Badge variant="outline" className="bg-teal-50 text-teal-800 border-teal-200 text-xs">
              {activeClinic?.name || 'Active Clinic'}
            </Badge>
          </div>
          <p className="text-slate-500 text-sm">
            Live patient triage and doctor consultation workflow
          </p>
        </div>

        <div className="flex items-center gap-2">
          <AuthorizedButton
            permission="APPOINTMENT_WRITE"
            tooltipMessage="You don't have access to add walk-in appointments"
            variant="outline"
            size="sm"
            onClick={() => navigate(buildUrl('appointments/new'))}
            className="text-xs font-semibold"
          >
            <Plus className="h-4 w-4 mr-1" /> Add Walk-in Visit
          </AuthorizedButton>

          <AuthorizedButton
            permission="CONSULTATION_WRITE"
            tooltipMessage="You don't have access to initiate direct consultations"
            size="sm"
            onClick={() => navigate(buildUrl('consultations/new'))}
            className="bg-teal-600 hover:bg-teal-700 font-bold text-white text-xs shadow-sm"
            data-testid="direct-consultation-btn"
          >
            <Stethoscope className="h-4 w-4 mr-1.5" /> Direct Encounter
          </AuthorizedButton>
        </div>
      </div>

      {/* KPI Counters Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card
          onClick={() => setActiveTab('waiting')}
          className={cn(
            'cursor-pointer transition-all border-l-4 hover:shadow-md',
            activeTab === 'waiting' ? 'border-l-blue-600 bg-blue-50/20' : 'border-l-blue-400'
          )}
        >
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Waiting in Queue</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{queue.waiting.length}</p>
            </div>
            <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center">
              <Hourglass className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card
          onClick={() => setActiveTab('in-progress')}
          className={cn(
            'cursor-pointer transition-all border-l-4 hover:shadow-md',
            activeTab === 'in-progress' ? 'border-l-amber-500 bg-amber-50/20' : 'border-l-amber-400'
          )}
        >
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">In Progress</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{queue.inProgress.length}</p>
            </div>
            <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
              <Activity className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card
          onClick={() => setActiveTab('completed')}
          className={cn(
            'cursor-pointer transition-all border-l-4 hover:shadow-md',
            activeTab === 'completed' ? 'border-l-emerald-600 bg-emerald-50/20' : 'border-l-emerald-400'
          )}
        >
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Completed Today</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{queue.completed.length}</p>
            </div>
            <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Search */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
          <div className="relative min-w-[260px] flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by patient name, ID, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 text-xs"
            />
          </div>

          <div className="flex items-center gap-3">
            {canViewAllConsultations && (
              <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
                <SelectTrigger className="h-9 w-[200px] text-xs">
                  <SelectValue placeholder="All Doctors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Doctors</SelectItem>
                  {doctors.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {formatDoctorName(d)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={loadQueue}
              className="text-xs border-slate-200"
            >
              Refresh Queue
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Triage Tabs & Queue Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1 border border-slate-200">
          <TabsTrigger value="waiting" className="text-xs data-[state=active]:bg-white data-[state=active]:text-slate-900 font-medium">
            Waiting ({waitingList.length})
          </TabsTrigger>
          <TabsTrigger value="in-progress" className="text-xs data-[state=active]:bg-white data-[state=active]:text-slate-900 font-medium">
            In Progress ({inProgressList.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="text-xs data-[state=active]:bg-white data-[state=active]:text-slate-900 font-medium">
            Completed ({completedList.length})
          </TabsTrigger>
        </TabsList>

        {/* WAITING QUEUE */}
        <TabsContent value="waiting" className="space-y-3">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
            </div>
          ) : waitingList.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-xl border border-slate-200">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
              <h3 className="font-semibold text-slate-800">Queue is Clear</h3>
              <p className="text-xs text-slate-500 mt-1">No patients currently waiting in this clinic.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {waitingList.map((item) => {
                const pInfo = getPatientInfo(item);
                const dInfo = getDoctorInfo(item);

                return (
                  <Card
                    key={item.id}
                    className="border-slate-200 hover:shadow-md transition-shadow"
                    data-testid={`queue-item-${item.id}`}
                  >
                    <CardContent className="p-5 flex flex-col justify-between h-full gap-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 bg-teal-100 text-teal-800 font-bold">
                            <AvatarFallback>
                              {(pInfo.firstName?.[0] || pInfo.displayName?.[0] || 'P').toUpperCase()}
                              {(pInfo.lastName?.[0] || pInfo.displayName?.split(' ')?.[1]?.[0] || '')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-bold text-sm text-slate-900">
                              {pInfo.displayName}
                            </p>
                            <p className="text-[11px] text-slate-500">
                              {pInfo.patientCode ? (
                                <span className="font-semibold text-slate-700 font-mono mr-1">
                                  {pInfo.patientCode}
                                </span>
                              ) : (
                                <span className="font-mono text-slate-600 mr-1">
                                  ID: {item.patientId ? (item.patientId.length > 12 ? `${item.patientId.slice(0, 8)}...` : item.patientId) : 'N/A'}
                                </span>
                              )}
                              • Age: {formatPatientAge(pInfo.age, pInfo.dateOfBirth)}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs font-semibold">
                          {item.timeFormatted || item.time || '09:00 AM'} Slot
                        </Badge>
                      </div>

                      <div className="bg-slate-50 p-3 rounded-lg text-xs space-y-1">
                        <p className="text-slate-700">
                          <strong>Doctor:</strong> {dInfo.displayName} {dInfo.specialization ? `(${dInfo.specialization})` : ''}
                        </p>
                        {item.notes && (
                          <p className="text-slate-500 truncate">
                            <strong>Notes:</strong> {item.notes}
                          </p>
                        )}
                        <div className="flex items-center gap-2 text-slate-400 text-[11px] pt-1">
                          <Clock className="h-3.5 w-3.5 text-amber-500" />
                          <span>Waiting ~{item.waitDurationMinutes || 15} mins</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(buildUrl(`patients/${item.patientId}`))}
                          className="text-xs text-slate-600 hover:text-slate-900"
                        >
                          <User className="h-3.5 w-3.5 mr-1" /> View History
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => navigate(buildUrl(`consultations/${item.id}?appointmentId=${item.id}&patientId=${item.patientId}`))}
                          className="bg-teal-600 hover:bg-teal-700 text-white text-xs shadow-sm font-semibold"
                          data-testid={`start-consultation-${item.id}`}
                        >
                          <Play className="h-3.5 w-3.5 mr-1 fill-white" /> Start Consultation
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* IN PROGRESS */}
        <TabsContent value="in-progress" className="space-y-3">
          {inProgressList.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-xl border border-slate-200">
              <Activity className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <h3 className="font-semibold text-slate-800">No Consultations In Progress</h3>
              <p className="text-xs text-slate-500 mt-1">Select a waiting patient above to begin an encounter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {inProgressList.map((item) => {
                const pInfo = getPatientInfo(item);
                const dInfo = getDoctorInfo(item);

                return (
                  <Card key={item.id} className="border-amber-200 bg-amber-50/20 shadow-sm">
                    <CardContent className="p-5 flex flex-col justify-between gap-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 bg-amber-100 text-amber-900 font-bold">
                            <AvatarFallback>
                              {(pInfo.firstName?.[0] || pInfo.displayName?.[0] || 'P').toUpperCase()}
                              {(pInfo.lastName?.[0] || pInfo.displayName?.split(' ')?.[1]?.[0] || '')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-bold text-sm text-slate-900">
                              {pInfo.displayName}
                            </p>
                            <p className="text-[11px] text-slate-500">
                              {pInfo.patientCode ? (
                                <span className="font-semibold text-slate-700 font-mono mr-1">
                                  {pInfo.patientCode}
                                </span>
                              ) : (
                                <span className="font-mono text-slate-600 mr-1">
                                  ID: {item.patientId ? (item.patientId.length > 12 ? `${item.patientId.slice(0, 8)}...` : item.patientId) : 'N/A'}
                                </span>
                              )}
                              • Age: {formatPatientAge(pInfo.age, pInfo.dateOfBirth)}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-amber-500 text-white text-xs">In Progress</Badge>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-amber-100">
                        <span className="text-xs text-slate-600 font-medium">{dInfo.displayName}</span>
                        <Button
                          size="sm"
                          onClick={() => navigate(buildUrl(`consultations/${item.id}?appointmentId=${item.id}&patientId=${item.patientId}`))}
                          className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
                        >
                          Resume Clinical Workspace →
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* COMPLETED */}
        <TabsContent value="completed" className="space-y-3">
          {completedList.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-xl border border-slate-200">
              <FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <h3 className="font-semibold text-slate-800">No Completed Consultations Today</h3>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {completedList.map((item) => {
                const pInfo = getPatientInfo(item);
                const dInfo = getDoctorInfo(item);

                return (
                  <Card key={item.id} className="border-slate-200 bg-white shadow-sm">
                    <CardContent className="p-5 flex flex-col justify-between gap-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 bg-emerald-100 text-emerald-800 font-bold">
                            <AvatarFallback>
                              {(pInfo.firstName?.[0] || pInfo.displayName?.[0] || 'P').toUpperCase()}
                              {(pInfo.lastName?.[0] || pInfo.displayName?.split(' ')?.[1]?.[0] || '')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-bold text-sm text-slate-900">
                              {pInfo.displayName}
                            </p>
                            <p className="text-[11px] text-slate-500">
                              {pInfo.patientCode ? (
                                <span className="font-semibold text-slate-700 font-mono mr-1">
                                  {pInfo.patientCode}
                                </span>
                              ) : (
                                <span className="font-mono text-slate-600 mr-1">
                                  ID: {item.patientId ? (item.patientId.length > 12 ? `${item.patientId.slice(0, 8)}...` : item.patientId) : 'N/A'}
                                </span>
                              )}
                              • Age: {formatPatientAge(pInfo.age, pInfo.dateOfBirth)}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                          Completed
                        </Badge>
                      </div>

                      <div className="text-xs text-slate-600 bg-slate-50 p-2.5 rounded-lg">
                        <p><strong>Consulting Doctor:</strong> {dInfo.displayName} {dInfo.specialization ? `(${dInfo.specialization})` : ''}</p>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-slate-100 gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(buildUrl(`consultations/${item.id}`))}
                          className="text-xs text-slate-700 hover:text-slate-900"
                        >
                          <FileText className="h-3.5 w-3.5 mr-1" /> Case Sheet
                        </Button>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(buildUrl(`patients/${item.patientId}`))}
                            className="text-xs border-slate-200 text-slate-600"
                          >
                            Profile
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedItemForInvoice(item);
                              setInvoiceModalOpen(true);
                            }}
                            className="text-xs bg-teal-600 hover:bg-teal-700 text-white font-semibold shadow-sm"
                          >
                            <Receipt className="h-3.5 w-3.5 mr-1" /> Bill / Invoice
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Invoice & Payment Modal */}
      {selectedItemForInvoice && (
        <InvoiceModal
          open={invoiceModalOpen}
          onOpenChange={(val) => {
            setInvoiceModalOpen(val);
            if (!val) setSelectedItemForInvoice(null);
          }}
          consultation={selectedItemForInvoice}
          patient={selectedItemForInvoice.patient}
          doctor={selectedItemForInvoice.doctor}
          clinicId={activeClinicId}
          hospitalId={hospitalId}
        />
      )}
    </div>
  );
};

export default ConsultationListPage;
